// 'use client';

import { Modifier } from '@/components/pages/itemview';
import { ModifierTypes } from '../types/modifiers';
import crypto from 'crypto';
import moment from 'moment-timezone';
import { AmountMoneyObj } from '@/components/modals/order-details-sheet';
// import React from 'react';
// import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
// import Tooltip from 'react-bootstrap/Tooltip';

export const basePath = process.env.NEXT_PUBLIC_BASE_URL;
export const googleApiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY;
export const googleAnalyticsId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
export const squareEnvironment = process.env.NEXT_PUBLIC_SQUARE_ENVIRONMENT;
export const squareLocationId = process.env.NEXT_PUBLIC_SQUARE_LOCATION_ID;
export const squareApplicationId = process.env.NEXT_PUBLIC_SQUARE_APPLICATION_ID;
export const redisUrl = process.env.NEXT_PUBLIC_REDIS_URL || 'redis://localhost:6379';

export const deConvertThumb = '6jSO5TXae7upYR2ymjJNcLRdcoq6f8rn';

export interface OpeningHour {
  day: string;
  // startTime: string;
  // endTime: string;
  isOpen: boolean;
  openingHours: Array<{
    startTime: string;
    endTime: string;
  }>;
}

export interface RangeLimit {
  amount: number;
  currency: string;
  rangeStart: number;
  rangeEnd: number;
  rangeUnit: string;
}

export interface PaymentLimits {
  allowCashOnDelieveryLimit: {amount: number, currency: string};
  allowCashOnPickupLimit: {amount: number, currency: string};
  allowDeliveryRanges: {max: number, freeRadius: number, unit: string};
  deliveryChargePer1km: {amount: number, currency: string};
  orderValueDeliveryLimit: {amount: number, currency: string};
  currency: string;
  ranges: {
    ranges: RangeLimit[];
  };
} 

export interface TippingDetails {
  enabled: boolean;
  defaultPercent: number;
  percentages: number[];
  defaultSmartTip: AmountMoneyObj | null;
  smartTippingEnabled: boolean;
  smartTips: Array<AmountMoneyObj>;
}
export interface CompanyDetails{
  locationId: string;
  name: string;
  tagline: string;
  email: string;
  additionalInfo: {
    openingHours: Array<OpeningHour>;
    deliveryRadius: {
      max: number;
      freeRadius: number;
      unit: string;
    };
  };
  address: {
    addressLine1: string;
    administrativeDistrictLevel1: string;
    country: string;
    locality: string;
    postalCode: string;
  };
  language: string;
  description: string;
  phone: string;
  socialMedia: {
    facebook: string;
    instagram: string;
    twitter: string;
  };
  website: string;
  timeZone: string;
  cordinates: {
    latitude: number;
    longitude: number;
  };
  paymentLimits: PaymentLimits;
  locationContactNo: string;
  shopStatus: string;
  serverTime: string;
  tippingDetails: TippingDetails;
}

export const availableLanguages = [
  {
      code: "en",
      name: "English"
  }, {
      code: "fr",
      name: "French"
  }
];

export const currencyCode = "USD";

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export function formatCurrency(value: number, currencyCode = 'USD', locale = 'en-US') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  }).format((value / 100));
}

//crypto md5 encryption with environment key
export function encryptData(data: string): string {
  const keyString = deConvertThumb;
  // Ensure the key is 32 bytes for aes-256-cbc
  try {
    const key = Buffer.alloc(32);
    Buffer.from(keyString).copy(key);
    const iv = Buffer.alloc(16, 0); // Use a fixed IV for demonstration; replace with your actual IV handling
    const cipher = crypto.createCipheriv('aes-256-cbc', key, iv);
    let encrypted = cipher.update(data, 'utf8', 'hex');
    encrypted += cipher.final('hex');
    return encrypted;
    
  } catch (error) {
    console.error('Encryption error:', error);
    return '';
  }
}

export function decryptData(encryptedData: string): string {
  const keyString = deConvertThumb;
  // Ensure the key is 32 bytes for aes-256-cbc
  try {
    const key = Buffer.alloc(32);
    Buffer.from(keyString).copy(key);
    const iv = Buffer.alloc(16, 0); // Use a fixed IV for demonstration; replace with your actual IV handling
    const decipher = crypto.createDecipheriv('aes-256-cbc', key, iv);
    let decrypted = decipher.update(encryptedData, 'hex', 'utf8');
    decrypted += decipher.final('utf8');
    return decrypted;

  } catch (error) {
    console.error('Decryption error:', error);
    return '';
  }
}

//thousands separator for numbers
export function formatNumberWithCommas(value: number): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

interface FormattedAddress {
  addressLine1: string;
  locality: string;
  administrativeDistrictLevel1: string;
  country: string;
}

export function convertFormattedAddressToAddress(formattedAddress: FormattedAddress | null): string {
  if (!formattedAddress) return '';
  const { addressLine1, locality, administrativeDistrictLevel1, country } = formattedAddress;
  return [
    addressLine1,
    locality,
    administrativeDistrictLevel1,
    country
  ].filter(Boolean).join(', ');
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

// Remove all spaces from a string
export function removeSpacesFromString(input: string): string {
  return input.replace(/\s+/g, '').toLowerCase();
}

export function timezoneDateConvert({ utcDate, timezone, format }: { utcDate: string; timezone?: string; format?: string }) {
  // Convert UTC date to the desired timezone
  // console.log(utcDate, timezone);

  const serverMoment = moment.tz(utcDate, (timezone || moment.tz.guess()));

  const browserTimezone = moment.tz.guess();
  const localMoment  = serverMoment.tz(browserTimezone);

  const localTime = localMoment.format(format || 'YYYY-MM-DDTHH:mm:ss');

  return localTime;
};

export function modifiersFilter (modifiers: Modifier[], modifierTypes: Array<ModifierTypes[keyof ModifierTypes]>) {
  const sendModifieres = [] as Array<Modifier>;

  modifierTypes.forEach((modifierType) => {
    if(modifierType === "base") {
      // Apply base filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("base") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "dietary") {
      // Apply dietary filters
      modifiers?.forEach((modifier) => {
        if((modifier.modifierName.toLowerCase().includes("dietary") || modifier.modifierName.toLowerCase().includes("gluten")) && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });

    } else if(modifierType === "add") {
      // Apply add filters
      modifiers?.forEach((modifier) => {
        if(
          (!modifier.modifierName.toLowerCase().includes("base") && !modifier.modifierName.toLowerCase().includes("dietary") && !modifier.modifierName.toLowerCase().includes("gluten")) &&
          ((modifier.modifierName.toLowerCase().includes("add") || modifier.modifierName.toLowerCase().includes("extra") || modifier.modifierName.toLowerCase().includes("topping"))
        ) && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });

    } else if(modifierType === "removal") {
      // Apply removal filters
      modifiers?.forEach((modifier) => {
        if(
          (!modifier.modifierName.toLowerCase().includes("base") && !modifier.modifierName.toLowerCase().includes("dietary") && !modifier.modifierName.toLowerCase().includes("gluten")) &&
          modifier.modifierName.toLowerCase().includes("remov") && modifier.mofireOptions.length > 0
        ) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "ingredient") {
      // Apply ingredient filters
      modifiers?.forEach((modifier) => {
        if(
          (!modifier.modifierName.toLowerCase().includes("base") && !modifier.modifierName.toLowerCase().includes("dietary") && !modifier.modifierName.toLowerCase().includes("gluten")) &&
          modifier.modifierName.toLowerCase().includes("ingred") && modifier.mofireOptions.length > 0
        ) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "notdietary") {
      // Apply not dietary filters
      modifiers?.forEach((modifier) => {
        if(!modifier.modifierName.toLowerCase().includes("dietary") && !modifier.modifierName.toLowerCase().includes("gluten") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "half1") {
      // Apply halfside1 filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("half 1") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "half2") {
      // Apply halfside2 filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("half 2") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "half1customs") {
      // Apply halfside1customs filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("pizza 1") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "half2customs") {
      // Apply halfside2customs filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("pizza 2") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    }
  });

  return sendModifieres;
}

// Capitalize the first letter of each word in a string
export function capitalizeText(text: string): string {
  return text.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}